import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Divider,
  Button,
  useTheme,
  alpha,
  Fade,
  Tooltip
} from '@mui/material';
import {
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { MaintenanceRecord, MaintenanceStatus } from '../../utils/types';
import { statusIcons } from '../../utils/constants';
import { statusLabels } from '../../utils/constants';
import { useCurrency } from '../../contexts/CurrencyContext';

interface RecentActivityProps {
  recentMaintenance: MaintenanceRecord[];
  loading?: boolean;
}

const statusConfig = {
  [MaintenanceStatus.PENDING]: {
    label: 'في الانتظار',
    color: 'warning' as const,
    icon: <ScheduleIcon />
  },
  [MaintenanceStatus.IN_PROGRESS]: {
    label: 'قيد التنفيذ',
    color: 'info' as const,
    icon: <BuildIcon />
  },
  [MaintenanceStatus.COMPLETED]: {
    label: 'مكتمل',
    color: 'success' as const,
    icon: <CheckCircleIcon />
  },
  [MaintenanceStatus.DELIVERED]: {
    label: 'تم التسليم',
    color: 'success' as const,
    icon: <CheckCircleIcon />
  },
  [MaintenanceStatus.CANCELLED]: {
    label: 'ملغي',
    color: 'error' as const,
    icon: <ErrorIcon />
  },
  [MaintenanceStatus.REFUNDED]: {
    label: 'مرتجع مع استرداد',
    color: 'error' as const,
    icon: <ErrorIcon />
  },
  [MaintenanceStatus.RETURNED]: {
    label: 'مرتجع مع رفض الجهاز',
    color: 'warning' as const,
    icon: <ErrorIcon />
  }
};

const getActivityColor = (status: MaintenanceStatus, theme: any) => {
  switch (status) {
    case MaintenanceStatus.PENDING:
      return theme.palette.warning.main;
    case MaintenanceStatus.IN_PROGRESS:
      return theme.palette.info.main;
    case MaintenanceStatus.COMPLETED:
    case MaintenanceStatus.DELIVERED:
      return theme.palette.success.main;
    case MaintenanceStatus.CANCELLED:
    case MaintenanceStatus.REFUNDED:
      return theme.palette.error.main;
    case MaintenanceStatus.RETURNED:
      return theme.palette.warning.main;
    default:
      return theme.palette.grey[500];
  }
};

const formatDate = (date: Date | string) => {
  try {
    if (!date) return 'تاريخ غير محدد';

    const d = new Date(date);
    if (isNaN(d.getTime())) return 'تاريخ غير صحيح';

    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now.getTime() - d.getTime()) / (1000 * 60));
      return `منذ ${diffInMinutes} دقيقة`;
    } else if (diffInHours < 24) {
      return `منذ ${diffInHours} ساعة`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `منذ ${diffInDays} يوم`;
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'تاريخ غير صحيح';
  }
};

const RecentActivity: React.FC<RecentActivityProps> = ({
  recentMaintenance,
  loading = false
}) => {
  const theme = useTheme();
  const { formatCurrency } = useCurrency();

  if (loading) {
    return (
      <Paper sx={{ p: 3, borderRadius: 3, height: 400 }}>
        <Typography variant="h6" gutterBottom>
          جاري تحميل الأنشطة الأخيرة...
        </Typography>
      </Paper>
    );
  }

  return (
    <Fade in timeout={1200}>
      <Paper
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            pb: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight="bold">
              الأنشطة الأخيرة
            </Typography>
            <Button
              component={RouterLink}
              to="/admin/maintenance-records"
              size="small"
              endIcon={<ArrowForwardIcon />}
              sx={{ borderRadius: 2 }}
            >
              عرض الكل
            </Button>
          </Box>
        </Box>

        {/* Activity List */}
        <List sx={{ p: 0 }}>
          {recentMaintenance.length > 0 ? (
            recentMaintenance
              .filter(record => record && record.status) // تصفية السجلات الفارغة
              .slice(0, 6)
              .map((record, index) => {
              // التأكد من وجود الحالة في statusConfig، وإلا استخدام قيمة افتراضية
              const config = statusConfig[record.status] || statusConfig[MaintenanceStatus.PENDING];
              const activityColor = getActivityColor(record.status, theme);

              return (
                <React.Fragment key={record.id || index}>
                  <ListItem
                    component={RouterLink}
                    to={record.orderNumber ? `/admin/maintenance/order-${record.orderNumber}` : `/admin/maintenance/${record.id}`}
                    sx={{
                      py: 2,
                      px: 3,
                      transition: 'all 0.2s ease',
                      textDecoration: 'none',
                      color: 'inherit',
                      '&:hover': {
                        bgcolor: alpha(activityColor, 0.05),
                        transform: 'translateX(-4px)'
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Tooltip title={config?.label || statusLabels[record.status]} placement="top">
                        <Avatar
                          sx={{
                            bgcolor: alpha(activityColor, 0.1),
                            color: activityColor,
                            border: `2px solid ${alpha(activityColor, 0.2)}`,
                            width: 40,
                            height: 40,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                        >
                          {statusIcons[record.status] || <ScheduleIcon />}
                        </Avatar>
                      </Tooltip>
                    </ListItemAvatar>

                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {record.customerName || 'عميل غير محدد'}
                          </Typography>
                          <Chip
                            label={config?.label || 'غير محدد'}
                            color={config?.color || 'default'}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.75rem',
                              height: 24
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {record.deviceType || 'جهاز غير محدد'} - {record.issue || 'غير محدد'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {record.receivedAt ? formatDate(record.receivedAt) : 'تاريخ غير محدد'}
                          </Typography>
                        </Box>
                      }
                    />

                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" fontWeight="bold" color={activityColor}>
                        {formatCurrency(record.finalCost || record.initialCost || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        التكلفة
                      </Typography>
                    </Box>
                  </ListItem>

                  {index < recentMaintenance.length - 1 && index < 5 && (
                    <Divider variant="inset" component="li" sx={{ ml: 9 }} />
                  )}
                </React.Fragment>
              );
            })
          ) : (
            <ListItem sx={{ py: 4, textAlign: 'center' }}>
              <ListItemText
                primary={
                  <Typography variant="h6" color="text.secondary">
                    لا توجد أنشطة حديثة
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    ستظهر الأنشطة الأخيرة هنا عند إضافة بيانات جديدة
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>

        {/* Footer */}
        {recentMaintenance.length > 6 && (
          <Box
            sx={{
              p: 2,
              textAlign: 'center',
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              bgcolor: alpha(theme.palette.background.default, 0.5)
            }}
          >
            <Button
              component={RouterLink}
              to="/admin/maintenance-records"
              variant="text"
              endIcon={<ArrowForwardIcon />}
              sx={{ borderRadius: 2 }}
            >
              عرض جميع الأنشطة ({recentMaintenance.length})
            </Button>
          </Box>
        )}
      </Paper>
    </Fade>
  );
};

export default RecentActivity;
